import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import httpClient, { ApiResponse } from '../httpClient';
import { formatErrorForDisplay } from '@/utils/errorHandling';
import {
    Position,
    PositionCloseInput,
    PositionFilters,
    PositionSummary,
    PositionUpdateInput,
} from '../../models/position';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';

// List positions with filtering
const fetchPositions = async (tradingAccountId?: string, filters?: PositionFilters) => {
    const params = { ...filters };
    if (tradingAccountId) {
        params.trading_account_id = tradingAccountId;
    }

    const { data } = await httpClient.get<PaginatedResponse<Position>>(
        API_ENDPOINTS.TRADER.POSITIONS.LIST,
        { params }
    );
    return data;
};

export const usePositions = (tradingAccountId?: string, filters?: PositionFilters) => {
    return useQuery<PaginatedResponse<Position>, Error>({
        queryKey: ['positions', 'list', tradingAccountId, filters],
        queryFn: () => fetchPositions(tradingAccountId, filters),
        // No polling — invalidated by useTradesSocket on trade/order events
        enabled: !!tradingAccountId,
    });
};

// Get position summary statistics
const fetchPositionSummary = async (tradingAccountId?: string, period?: string) => {
    const params: any = {};
    if (tradingAccountId) params.trading_account_id = tradingAccountId;
    if (period) params.period = period;

    const { data } = await httpClient.get<ApiResponse<PositionSummary>>(
        API_ENDPOINTS.TRADER.POSITIONS.SUMMARY,
        { params }
    );
    return data.data;
};

export const usePositionSummary = (tradingAccountId?: string, period?: string) => {
    return useQuery<PositionSummary, Error>({
        queryKey: [API_ENDPOINTS.TRADER.POSITIONS.SUMMARY, tradingAccountId, period],
        queryFn: () => fetchPositionSummary(tradingAccountId, period),
        refetchInterval: 30000, // Refresh every 30 seconds
        enabled: !!tradingAccountId,
    });
};

// Get specific position by ID
const fetchPosition = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Position>>(
        API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)
    );
    return data.data;
};

export const usePosition = (id: string) => {
    return useQuery<Position, Error>({
        queryKey: [API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)],
        queryFn: () => fetchPosition(id),
        enabled: !!id,
        // No polling — invalidated by useTradesSocket on trade/order events
    });
};

// Close position (full or partial)
export const useClosePosition = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation<Position, Error, { id: string; input?: PositionCloseInput }>({
        mutationFn: async ({ id, input }) => {
            const { data } = await httpClient.post<ApiResponse<Position>>(
                API_ENDPOINTS.TRADER.POSITIONS.CLOSE(id),
                input || {}
            );
            return data.data;
        },
        onSuccess: (_, { id }) => {
            // Show success message
            enqueueSnackbar('Position closed successfully', { variant: 'success' });

            // Invalidate related queries
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)],
            });
            queryClient.invalidateQueries({ queryKey: ['positions', 'list'] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.POSITIONS.SUMMARY] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.TRADES.LIST] });
        },
        onError: error => {
            console.error('Failed to close position:', error);

            // Use the utility function to format the error message
            const { message: errorMessage, isValidationError } = formatErrorForDisplay(error);

            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: isValidationError ? 8000 : 6000,
                style: isValidationError ? { whiteSpace: 'pre-line' } : undefined,
            });
        },
    });
};

// Update position stop loss
export const useUpdatePositionStopLoss = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation<Position, Error, { id: string; stop_loss_price: number }>({
        mutationFn: async ({ id, stop_loss_price }) => {
            const { data } = await httpClient.put<ApiResponse<Position>>(
                API_ENDPOINTS.TRADER.POSITIONS.UPDATE_STOP_LOSS(id),
                { stop_loss_price }
            );
            return data.data;
        },
        onSuccess: (_, { id }) => {
            enqueueSnackbar('Stop loss updated successfully', { variant: 'success' });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)],
            });
            queryClient.invalidateQueries({ queryKey: ['positions', 'list'] });
        },
        onError: error => {
            console.error('Failed to update stop loss:', error);
            const { message: errorMessage, isValidationError } = formatErrorForDisplay(error);
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: isValidationError ? 8000 : 6000,
                style: isValidationError ? { whiteSpace: 'pre-line' } : undefined,
            });
        },
    });
};

// Update position take profit
export const useUpdatePositionTakeProfit = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation<Position, Error, { id: string; take_profit_price: number }>({
        mutationFn: async ({ id, take_profit_price }) => {
            const { data } = await httpClient.put<ApiResponse<Position>>(
                API_ENDPOINTS.TRADER.POSITIONS.UPDATE_TAKE_PROFIT(id),
                { take_profit_price }
            );
            return data.data;
        },
        onSuccess: (_, { id }) => {
            enqueueSnackbar('Take profit updated successfully', { variant: 'success' });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)],
            });
            queryClient.invalidateQueries({ queryKey: ['positions', 'list'] });
        },
        onError: error => {
            console.error('Failed to update take profit:', error);
            const { message: errorMessage, isValidationError } = formatErrorForDisplay(error);
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: isValidationError ? 8000 : 6000,
                style: isValidationError ? { whiteSpace: 'pre-line' } : undefined,
            });
        },
    });
};

// Update position (both stop loss and take profit)
export const useUpdatePosition = () => {
    const queryClient = useQueryClient();
    return useMutation<Position, Error, { id: string; input: PositionUpdateInput }>({
        mutationFn: async ({ id, input }) => {
            // Use specific endpoints for stop loss or take profit updates
            if (input.stop_loss_price && !input.take_profit_price) {
                const { data } = await httpClient.put<ApiResponse<Position>>(
                    API_ENDPOINTS.TRADER.POSITIONS.UPDATE_STOP_LOSS(id),
                    { stop_loss_price: input.stop_loss_price }
                );
                return data.data;
            } else if (input.take_profit_price && !input.stop_loss_price) {
                const { data } = await httpClient.put<ApiResponse<Position>>(
                    API_ENDPOINTS.TRADER.POSITIONS.UPDATE_TAKE_PROFIT(id),
                    { take_profit_price: input.take_profit_price }
                );
                return data.data;
            } else {
                // Update both - would need a combined endpoint or make two calls
                throw new Error(
                    'Use specific hooks for individual updates or implement combined update endpoint'
                );
            }
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.POSITIONS.DETAILS(id)],
            });
            queryClient.invalidateQueries({ queryKey: ['positions', 'list'] });
        },
    });
};

// Helper hooks for common position operations

// Get open positions only
export const useOpenPositions = (tradingAccountId?: string) => {
    return usePositions(tradingAccountId, { status: 'open' });
};

// Get positions for a specific instrument
export const useInstrumentPositions = (tradingAccountId?: string, instrumentId?: string) => {
    return usePositions(
        tradingAccountId,
        instrumentId ? { instrument_id: instrumentId } : undefined
    );
};

// Get profitable positions only
export const useProfitablePositions = (tradingAccountId?: string) => {
    return useQuery<Position[], Error>({
        queryKey: ['positions', 'profitable', tradingAccountId],
        queryFn: async () => {
            const result = await fetchPositions(tradingAccountId);
            const profitable = result.data.filter(p => p.unrealized_pnl > 0);
            const losing = result.data.filter(p => p.unrealized_pnl < 0);

            // Return profitable positions sorted by PnL
            return profitable.sort((a, b) => b.unrealized_pnl - a.unrealized_pnl);
        },
        // No polling — invalidated by useTradesSocket on trade/order events
        enabled: !!tradingAccountId,
    });
};

// Get position exposure by currency/instrument
export const usePositionExposure = (tradingAccountId?: string) => {
    return useQuery<Record<string, { long: number; short: number; net: number }>, Error>({
        queryKey: ['positions', 'exposure', tradingAccountId],
        queryFn: async () => {
            const result = await fetchPositions(tradingAccountId, { status: 'open' });
            const exposure: Record<string, { long: number; short: number; net: number }> = {};

            result.data.forEach(position => {
                const symbol = position.instrument.symbol;
                if (!exposure[symbol]) {
                    exposure[symbol] = { long: 0, short: 0, net: 0 };
                }

                if (position.side === 'long') {
                    exposure[symbol].long += position.lot_size;
                } else {
                    exposure[symbol].short += position.lot_size;
                }
                exposure[symbol].net = exposure[symbol].long - exposure[symbol].short;
            });

            return exposure;
        },
        // No polling — invalidated by useTradesSocket on trade/order events
        enabled: !!tradingAccountId,
    });
};
